<template>
  <div class="spl-edit-template__step spl-book-details">
    <div class="row">
      <!-- *********************** Dimensions  *********************************** -->
      <div class="col2">
        <form-label>Dimensions</form-label>
        <form-input
          v-model="bookingData.book_parcel_model.Length"
          :invalid="$v.bookingData.book_parcel_model.Length.$anyError"
          placeholder="Length (cm)"
          type="float"
          class="spl-book-details__length"
          @input="isValidCheck" />
        <span v-if="!$v.bookingData.book_parcel_model.Length.integer" class="error">Length has to be a number with no decimal places</span>
      </div>
      <div class="col2">
        <form-label />
        <form-input
          v-model="bookingData.book_parcel_model.Width"
          :invalid="$v.bookingData.book_parcel_model.Width.$anyError"
          placeholder="Width (cm)"
          type="float"
          class="spl-book-details__width"
          @input="isValidCheck" />
        <span v-if="!$v.bookingData.book_parcel_model.Width.integer" class="error">Width has to be a number with no decimal places</span>
      </div>
      <div class="col2">
        <form-label />
        <form-input
          v-model="bookingData.book_parcel_model.Height"
          :invalid="$v.bookingData.book_parcel_model.Height.$anyError"
          placeholder="Height (cm)"
          type="float"
          class="spl-book-details__height"
          @input="isValidCheck" />
        <span v-if="!$v.bookingData.book_parcel_model.Height.integer" class="error">Height has to be a number with no decimal places</span>
      </div>
      <!-- *************************** Security ***************************-->
      <div class="col3 spl-book-details__security">
        <form-label>Security</form-label>
        <form-checkbox
          v-if="show_dgr"
          :checked="bookingData.book_parcel_model.Dangerous_Goods"
          class="spl-book-details__dangerous-goods"
          @checkboxChanged="dangerous_goods_set">
          Dangerous Good
        </form-checkbox>
        <br>
        <form-checkbox
          v-if="show_xrayed"
          :checked="bookingData.book_parcel_model.XRayed"
          class="spl-book-details__xrayed"
          @checkboxChanged="xrayed_set">
          X-Rayed
        </form-checkbox>
        <br>
        <form-checkbox
          v-if="show_lmu"
          :checked="bookingData.book_parcel_model.Last_Mile_Upgrade"
          class="spl-book-details__lmu"
          @checkboxChanged="lmu_set">
          Last Mile Upgrade
        </form-checkbox>
      </div>
    </div>
    <div class="row">
      <!-- *********************** Volume  *********************************** -->
      <!-- Only Required if no dimensions given-->
      <div class="col2">
        <form-label>Volume</form-label>
        <form-input
          v-model="bookingData.book_parcel_model.Volume"
          :invalid="$v.bookingData.book_parcel_model.Volume.$anyError"
          placeholder="Volume (m3)"
          type="float"
          class="spl-book-details__volume"
          @input="isValidCheck" />
        <span v-if="!$v.bookingData.book_parcel_model.Volume.decimal" class="error">Volume has to be a number</span>
      </div>

      <!-- *********************** Weight  *********************************** -->
      <div class="col2">
        <form-label>Weight</form-label>
        <form-input
          v-model="bookingData.book_parcel_model.Weight"
          :invalid="$v.bookingData.book_parcel_model.Weight.$error"
          type="float"
          class="spl-book-details__weight"
          placeholder="Weight (kg)"
          @input="isValidCheck"
          @blur="$v.bookingData.book_parcel_model.Weight.$touch()" />
        <span v-if="$v.bookingData.book_parcel_model.Weight.$error && !$v.bookingData.book_parcel_model.Weight.decimal" class="error">Weight has to be a number</span>
        <span v-if="$v.bookingData.book_parcel_model.Weight.$error && !$v.bookingData.book_parcel_model.Weight.required" class="error">Weight is required</span>
        <span v-if="$v.bookingData.book_parcel_model.Weight.$error && !$v.bookingData.book_parcel_model.Weight.biggerThanZero" class="error">Weight has to be bigger than zero</span>
      </div>
    </div>

    <!-- Next step footer -->
    <general-footer
      :cancel-visible="$mq === 'sm'"
      :positive-label="nextStepLabel"
      negative-label="Back"
      @negativeClicked="previousStep"
      @positiveClicked="nextStep" />
  </div>
</template>

<script>
import Helpers from '@/helpers.vue'
import { integer, decimal, required } from 'vuelidate/lib/validators'
import { mapActions } from 'vuex'

export default {
  name: 'BookingParcelDetails',
  mixins: [Helpers],
  props: ['bookingData', 'nextStepLabel'],
  data () {
    return {
      show_dgr: true,
      show_xrayed: true,
      show_lmu: true
    }
  },
  validations: {
    bookingData: {
      book_parcel_model: {
        Width: {
          integer
        },
        Height: {
          integer
        },
        Length: {
          integer
        },
        Volume: {
          decimal
        },
        Weight: {
          required,
          decimal,
          biggerThanZero (value) {
            if (isNaN(parseFloat(value))) {
              return true
            }
            return value > 0
          }
        }
      }
    }
  },
  watch: {
    bookingData: {
      handler: function (val, oldVal) {
        this.isValidCheck()
      },
      deep: true
    }
  },
  mounted () {
    this.$watch(vm => [vm.bookingData.book_parcel_model.Country_ID, vm.bookingData.book_parcel_model.Customer_ID, vm.bookingData.book_parcel_model.Is_Forward], val => {
      if (this.bookingData.book_parcel_model.Country_ID !== -1 && this.bookingData.book_parcel_model.Customer_ID !== -1) {
        this.getProfiler(this.bookingData.book_parcel_model.Customer_ID, this.bookingData.book_parcel_model.Country_ID, this.bookingData.book_parcel_model.Is_Forward)
      }
    },
    {
      immediate: true,
      deep: true
    })
  },
  methods: {
    ...mapActions('profiler', ['loadProfiler']),
    // Set the dangerous goods
    dangerous_goods_set (item) {
      this.bookingData.book_parcel_model.Dangerous_Goods = item
    },

    // Set the xray
    xrayed_set (item) {
      this.bookingData.book_parcel_model.XRayed = item
    },

    getProfiler (customer_id, country_id, is_forward) {
      var profileFilter = { Customer_ID: customer_id, Country_ID: country_id, Is_Forward: is_forward }
      this.loadProfiler(profileFilter).then(() => {
        this.profileData = JSON.parse(window.sessionStorage.getItem('Profiler_List'))
        if (this.profileData !== null && this.profileData.Fields.length > 0) {
          this.address_id = []
          const dgr = this.profileData.Fields.find(el => el.Name === 'Dangerous Good')
          const xrayed = this.profileData.Fields.find(el => el.Name === 'X-Rayed')
          const lmu = this.profileData.Fields.find(el => el.Name === 'Last Mile Upgrade')
          if (dgr) {
            this.show_dgr = dgr.Visible
          }
          if (xrayed) {
            this.show_xrayed = xrayed.Visible
          }
          if (lmu) {
            this.show_lmu = lmu.Visible
          }
        }
      }).catch((error) => {
        window.bus.$emit('openErrorModal', error.ExceptionMessage)
      })
    },

    // set the last mile upgrade
    lmu_set (value) {
      this.bookingData.book_parcel_model.Last_Mile_Upgrade = value
    },
    isValidCheck () {
      this.$v.bookingData.book_parcel_model.Width.$touch()
      this.$v.bookingData.book_parcel_model.Height.$touch()
      this.$v.bookingData.book_parcel_model.Length.$touch()
      this.$v.bookingData.book_parcel_model.Volume.$touch()
      this.$v.bookingData.book_parcel_model.Weight.$touch()
    },
    nextStep () {
      this.isValidCheck()
      if (!this.$v.$invalid) {
        this.$emit('nextStep', this.bookingData)
      }
    },
    isValid () {
      return !this.$v.$invalid
    },
    previousStep () {
      this.$emit('previousStep', this.bookingData)
    }
  }
}
</script>

<style lang="scss">
@import '../../variables.scss';
@include mobile() {
  .spl-book-details {
    &__security {
      margin: 10px 0;
    }
    &__volume {
      margin-bottom: 10px;
    }
  }
}
</style>
